import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Gift, Smartphone, Percent, Brain, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';

const LandingPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Auto-redirect authenticated users to swiping interface
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/discover');
    }
  }, [isAuthenticated, navigate]);

  const handleStartSwiping = () => {
    if (isAuthenticated) {
      navigate('/discover');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-accent/20" />

        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-secondary/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 max-w-6xl mx-auto text-center space-y-8 animate-slide-up">
          {/* Logo/Brand */}
          <div className="inline-flex items-center gap-2 px-6 py-3 glass rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-primary animate-glow" />
            <span className="text-sm font-semibold tracking-wider">SWIPE. DISCOVER. SAVE.</span>
          </div>

          {/* Main heading */}
          <h1 className="text-6xl md:text-8xl font-bold leading-tight">
            <span className="text-gradient">Jaalu</span>
          </h1>

          {/* Tagline */}
          <p className="text-3xl md:text-5xl font-bold text-foreground/90">
            We Weave, <span className="text-gradient">You Save</span>
          </p>

          {/* Description */}
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Swipe through genuine products at massive discounts—plus exciting mystery cards for extra surprises! 💜
          </p>

          {/* CTA — opens login/signup when not authenticated */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <button
              onClick={handleStartSwiping}
              className="btn-cosmic text-lg px-8 py-6 rounded-full font-semibold hover:scale-105 transition-transform glow-primary group"
            >
              {isAuthenticated ? 'Continue Swiping' : 'Start Swiping'}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 pt-16 max-w-2xl mx-auto">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-gradient">80%</div>
              <div className="text-sm text-muted-foreground">Avg Savings</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-gradient">10k+</div>
              <div className="text-sm text-muted-foreground">Happy Shoppers</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-gradient">50k+</div>
              <div className="text-sm text-muted-foreground">Products</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-32 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-20 animate-slide-down">
            <h2 className="text-5xl md:text-6xl font-bold">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Shop genuine products with surprise mystery cards for extra fun
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative animate-scale-in" style={{ animationDelay: '0s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent opacity-0 group-hover:opacity-10 transition-opacity rounded-3xl" />

                <div className="absolute top-4 right-4 text-6xl font-bold text-foreground/5">1</div>

                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent p-0.5">
                    <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                      <Smartphone className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 relative">Swipe & Discover</h3>
                <p className="text-muted-foreground leading-relaxed relative">
                  Browse genuine products you can see. Swipe right to like, left to skip. Simple!
                </p>
              </div>
            </div>

            <div className="group relative animate-scale-in" style={{ animationDelay: '0.2s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-accent to-secondary opacity-0 group-hover:opacity-10 transition-opacity rounded-3xl" />

                <div className="absolute top-4 right-4 text-6xl font-bold text-foreground/5">2</div>

                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-secondary p-0.5">
                    <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                      <Gift className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 relative">Mystery Surprises</h3>
                <p className="text-muted-foreground leading-relaxed relative">
                  Some cards are mystery surprises—revealed after purchase for extra excitement!
                </p>
              </div>
            </div>

            <div className="group relative animate-scale-in" style={{ animationDelay: '0.4s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary to-primary opacity-0 group-hover:opacity-10 transition-opacity rounded-3xl" />

                <div className="absolute top-4 right-4 text-6xl font-bold text-foreground/5">3</div>

                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-primary p-0.5">
                    <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 relative">Save & Enjoy</h3>
                <p className="text-muted-foreground leading-relaxed relative">
                  Enjoy massive discounts on real products, with occasional mystery deals for thrill-seekers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-4 mb-20">
            <h2 className="text-5xl md:text-6xl font-bold">
              Why Choose <span className="text-gradient">Jaalu?</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="group animate-scale-in" style={{ animationDelay: '0s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent opacity-0 group-hover:opacity-20 transition-opacity rounded-3xl blur-xl" />

                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent p-0.5 mb-6 relative">
                  <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                    <Smartphone className="w-10 h-10 text-primary" />
                  </div>
                </div>

                <h3 className="text-3xl font-bold mb-4 relative">Gamified Shopping</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed relative">
                  Turn shopping into an exciting game with our Tinder-like swipe interface for real products.
                </p>

                <ul className="space-y-3 relative">
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Interactive swipe mechanics</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Genuine products you can see</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Occasional mystery surprises</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="group animate-scale-in" style={{ animationDelay: '0.15s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-accent to-secondary opacity-0 group-hover:opacity-20 transition-opacity rounded-3xl blur-xl" />

                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent to-secondary p-0.5 mb-6 relative">
                  <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                    <Percent className="w-10 h-10 text-primary" />
                  </div>
                </div>

                <h3 className="text-3xl font-bold mb-4 relative">Massive Savings</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed relative">
                  Get premium products at incredible discounts, with mystery deals for extra excitement.
                </p>

                <ul className="space-y-3 relative">
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Up to 80% off retail prices</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Premium product quality</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Mystery cards for thrill-seekers</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="group animate-scale-in" style={{ animationDelay: '0.3s' }}>
              <div className="glass rounded-3xl p-8 h-full hover:scale-105 transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary to-primary opacity-0 group-hover:opacity-20 transition-opacity rounded-3xl blur-xl" />

                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-secondary to-primary p-0.5 mb-6 relative">
                  <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                    <Brain className="w-10 h-10 text-primary" />
                  </div>
                </div>

                <h3 className="text-3xl font-bold mb-4 relative">AI Recommendations</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed relative">
                  Our smart algorithm learns your preferences and suggests perfect products.
                </p>

                <ul className="space-y-3 relative">
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Personalized suggestions</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Better matches over time</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">Early access to deals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-background to-transparent" />

        <div className="absolute top-10 right-10 w-72 h-72 bg-accent/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-secondary/30 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />

        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-6 py-3 glass rounded-full mb-4">
            <Users className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold">10,000+ HAPPY SHOPPERS</span>
          </div>

          <h2 className="text-5xl md:text-7xl font-bold leading-tight">
            Ready to Start{" "}
            <span className="text-gradient">Saving Big?</span>
          </h2>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join thousands discovering genuine products at unbeatable prices—with mystery surprises along the way!
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <button
              onClick={handleStartSwiping}
              className="btn-cosmic text-lg px-12 py-6 rounded-full font-semibold hover:scale-105 transition-transform glow-primary group"
            >
              {isAuthenticated ? 'Continue Swiping' : 'Start Swiping Now'}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-8 pt-12 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary animate-glow" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-accent animate-glow" />
              <span>Free to start</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-secondary animate-glow" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default LandingPage;
